import { Fade } from '@heidi/design-system/fade';
import { useVerticalScrollFade } from '@heidi/design-system/hooks/use-vertical-scroll-fade';
import { AudioLines } from 'lucide-react';
import { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { parseQuoteShapedTranscriptExcerpts } from './parse-transcript-excerpts';

const messages = defineMessages({
  excerptsHeader: {
    defaultMessage:
      '{count, plural, one {# excerpt from the session transcript} other {# excerpts from the session transcript}}',
    id: 'xn0z7BRtn3',
    description:
      'Header above the transcript quotes in the inline View transcript source panel',
  },
});

/**
 * Structured rendering of a View-transcript-source insight: a header stating
 * how many transcript excerpts support the highlighted text, then each
 * excerpt as a quoted row with its session timestamp when available — so
 * it's unmistakable the content comes from the session transcript.
 */
export const InlineEditTranscriptExcerpts = ({ text }: { text: string }) => {
  const intl = useIntl();
  const excerpts = parseQuoteShapedTranscriptExcerpts(text);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { canScrollUp, canScrollDown } = useVerticalScrollFade(
    scrollRef,
    excerpts.length > 0,
    [text, excerpts.length]
  );

  if (excerpts.length === 0) return null;

  return (
    <div className="mt-1.5 flex flex-col gap-1.5" data-testid="inline-edit-excerpts">
      <div className="flex items-center gap-1.5 text-xs font-medium text-text-secondary">
        <AudioLines size={12} className="shrink-0" />
        {intl.formatMessage(messages.excerptsHeader, { count: excerpts.length })}
      </div>
      <div className="relative [--fade-bg:hsl(var(--twc-surface))]">
        <div
          ref={scrollRef}
          data-testid="inline-edit-excerpts-scroll"
          className="scrollbar-subtle flex max-h-48 flex-col gap-1 overflow-y-auto"
        >
          {excerpts.map((excerpt, index) => (
            <div
              key={index}
              className="flex items-start gap-2 rounded-md bg-surface-selected p-2 text-sm"
            >
              {excerpt.timestamp && (
                <span className="mt-0.5 shrink-0 rounded bg-surface px-1.5 py-0.5 text-xs tabular-nums text-text-secondary">
                  {excerpt.timestamp}
                </span>
              )}
              <span className="whitespace-pre-wrap text-text-primary">
                “{excerpt.text}”
              </span>
            </div>
          ))}
        </div>
        {canScrollUp ? (
          <Fade
            side="top"
            background="var(--fade-bg)"
            className="inset-x-0 top-0 h-6"
            data-testid="inline-edit-excerpts-scroll-top-fade"
          />
        ) : null}
        {canScrollDown ? (
          <Fade
            side="bottom"
            background="var(--fade-bg)"
            className="inset-x-0 bottom-0 h-6"
            data-testid="inline-edit-excerpts-scroll-bottom-fade"
          />
        ) : null}
      </div>
    </div>
  );
};
